import { useAccountingStatementsTable } from "../../../context/AccountingStatementsTableContext";
import { useGetAccountingStatementsListQuery } from "../../../redux/api/reconciliationsApiSlice";
import { isEmpty } from "lodash";
import { Button, Descriptions, Space, Table, TableProps } from "antd";
import { AccountingStatement, GetAccountingStatementsListParams } from "../../../types/reconciliation";
import React, { Key, useMemo } from "react";
import { getColumnDateProps, getStandardFilter } from "../../tables/negotiationsTables/columnsUtils";
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, SyncOutlined } from "@ant-design/icons";
import FilterBySearch from "../../tables/negotiationsTables/generalTable/FilterBySearch";
import { DownloadAccountingStatementDocumentsButton } from "./DownloadAccountingStatementDocumentsButton";
import { DeleteAccountingStatementButton } from "./DeleteAccountingStatementButton";
import { printValue } from "utils/reconciliationTableUtils";

export const AccountingStatementsTable = ({ onRecordDetailClick, defaultFilters }: {
  onRecordDetailClick?: (record: AccountingStatement) => void,
  defaultFilters?: GetAccountingStatementsListParams
}) => {

  const {
    page,
    pageSize,
    reconciliationJobStatus,
    roreJobStatus,
    dateCreation,
    ecPeriod,
    ecNumber,
    documentName,
    toBeReconciled,
    setPage,
    setPageSize,
    setOrdering,
    setDateCreation,
    setEcPeriod,
    setEcNumber,
    setDocumentName,
    setReconciliationJobStatus,
    setRoreJobStatus,
    setToBeReconciled,
    getParamsForApiRequest
  } = useAccountingStatementsTable();

  const {
    data: reconciliationList,
    isLoading: isLoadingReconciliationList,
    isFetching: isFetchingReconciliationList
  } = useGetAccountingStatementsListQuery(getParamsForApiRequest(defaultFilters), { pollingInterval: 120000 });

  const cols: TableProps<AccountingStatement>["columns"] = useMemo(() => ([
    {
      key: "to_be_reconciled",
      dataIndex: "to_be_reconciled",
      title: "Riconciliabile",
      filteredValue: toBeReconciled,
      ...getStandardFilter([{ text: "Sì", value: "true" }, {
        text: "No",
        value: "false"
      }]),
      render: (value) => {
        return <>{value ? "Sì" : "No"}</>;
      }
    },
    {
      key: "rore_job_status",
      dataIndex: "rore_job_status",
      title: "Stato lettura EC",
      filteredValue: roreJobStatus,
      ...getStandardFilter([{ text: "In elaborazione", value: "P" }, {
        text: "Completato",
        value: "D"
      }, { text: "In errore", value: "E" }]),
      width: 80,
      ellipsis: true,
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (value) => {
        switch (value) {
          case "P":
            return <SyncOutlined spin style={{ color: "#1677ff" }} />;
          case "D":
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
          case "E":
            return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
          default:
            return <></>;
        }
      }
    },
    {
      key: "date_creation",
      dataIndex: "date_creation",
      title: "Data caricamento",
      sorter: { multiple: 1 },
      filteredValue: dateCreation,
      ...getColumnDateProps("date_creation", "day"),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", { dateTemplate: "DD/MM/YYYY HH:mm" })
    },
    {
      key: "ec_period",
      dataIndex: "ec_period",
      title: "Data EC",
      sorter: { multiple: 4 },
      filteredValue: ecPeriod,
      ...getColumnDateProps("ec_period", "month"),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", {
        dateTemplate: "MMMM YYYY",
        status: record.rore_job_status ?? undefined
      })
    },
    {
      key: "ec_number",
      dataIndex: "ec_number",
      title: "Numero EC",
      sorter: { multiple: 5 },
      filteredValue: ecNumber,
      filterDropdown: (props) => {
        return <FilterBySearch props={props}
                               placeholder={"Filtra per numero EC..."} />;
      },
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { status: record.rore_job_status ?? undefined })
    },
    {
      key: "document_name",
      dataIndex: "document_name",
      title: "Nome documento",
      sorter: { multiple: 2 },
      filteredValue: documentName,
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per il nome documento..."} />;
      },
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { status: record.rore_job_status ?? undefined })
    },
    {
      key: "reconciliation_job_status",
      dataIndex: "reconciliation_job_status",
      title: "Stato riconciliazione",
      filteredValue: reconciliationJobStatus,
      ...getStandardFilter([{ text: "In elaborazione", value: "P" }, {
        text: "Completato",
        value: "D"
      }, { text: "In errore", value: "E" }]),
      width: 80,
      ellipsis: true,
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (value) => {
        switch (value) {
          case "P":
            return <SyncOutlined spin style={{ color: "#1677ff" }} />;
          case "D":
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
          case "E":
            return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
          default:
            return <></>;
        }
      }
    },
    {
      title: "Azioni",
      key: "actions",
      fixed: "right",
      onCell: () => ({ style: { justifyContent: "center", alignItems: "center", display: "flex" } }),
      render: (_, record) => <Space>
        <Button
          title={"Vedi dettaglio titoli"}
          disabled={!(record.rore_job_status === "D" && record.has_titles)}
          type={"text"}
          icon={<EyeOutlined />}
          onClick={() => {
            onRecordDetailClick?.(record);
          }}
        />
        <DownloadAccountingStatementDocumentsButton accountingStatement={record} />
        <DeleteAccountingStatementButton uuid={record.uuid} disabled={!record.is_deletable} />
      </Space>
    }
  ]), [dateCreation, documentName, ecNumber, ecPeriod, onRecordDetailClick, reconciliationJobStatus, roreJobStatus, toBeReconciled]);


  return (
    <>
      <Table<AccountingStatement>
        dataSource={reconciliationList?.results ?? []}
        columns={cols}
        loading={isLoadingReconciliationList || isFetchingReconciliationList}
        scroll={{ x: true }}
        rowKey={"uuid"}
        pagination={{
          current: page,
          pageSize: pageSize,
          showSizeChanger: true,
          total: reconciliationList?.count ?? 0,
          showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} estratti conto`,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          }
        }}
        onChange={(_, filters, sorter) => {
          // sorter può essere un oggetto o un array
          let orderingString = "";
          if (Array.isArray(sorter)) {
            orderingString = sorter
              .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
              .join(",");
          } else if (sorter.order) {
            orderingString = sorter.order === "descend" ? `-${sorter.field}` : `${sorter.field}`;
          }

          setOrdering(orderingString);
          // Gestione filtri
          if (filters) {
            // Mappa delle funzioni setter per ogni tipo di filtro
            const filterSetters: Record<string, (value: Key[]) => void> = {
              ec_period: setEcPeriod,
              ec_number: setEcNumber,
              document_name: setDocumentName,
              rore_job_status: setRoreJobStatus,
              reconciliation_job_status: setReconciliationJobStatus,
              date_creation: setDateCreation,
              to_be_reconciled: setToBeReconciled
            };

            // Applica i setter per ogni filtro
            Object.entries(filterSetters).forEach(([filterName, setter]) => {
              const filterValue = filters[filterName];
              setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
            });
          }
        }}
      />
    </>
  );
};

export const AccountingStatementDescription = ({ accountingStatement }: {
  accountingStatement: AccountingStatement
}) => {
  return (
    <Descriptions
      style={{ margin: "1rem 0" }}
      labelStyle={{ fontWeight: "bold" }}
      bordered={true}
    >
      <Descriptions.Item
        label={"Totale provvigioni"}
        className={`${!accountingStatement.debit_sum_validation ? "warning-table-cell" : ""}`}>{printValue(accountingStatement.total_debit, "currency")}</Descriptions.Item>
      <Descriptions.Item
        label={"Totale premio lordo"}
        className={`${!accountingStatement.credit_sum_validation ? "warning-table-cell" : ""}`}>{printValue(accountingStatement.total_credit, "currency")}</Descriptions.Item>
      <Descriptions.Item
        label={"Saldo"}
        className={`${!accountingStatement.balance_validation ? "warning-table-cell" : ""}`}>{printValue(accountingStatement.balance, "currency")}</Descriptions.Item>
      <Descriptions.Item
        label={"Ritenuta d'acconto"}>{printValue(accountingStatement.withholding_tax, "currency")}</Descriptions.Item>
    </Descriptions>
  );
};